import useLoyaltyChooseDoubleRewards from '../hooks/useLoyaltyChooseDoubleRewards'
import TextLink from 'LuxKit/TextLink'
import { useCallback, type ReactNode } from 'react'
import { useIntl } from 'react-intl'
import { CHOOSE_DOUBLE_REWARDS_SLUG } from '../loyaltyChooseDoubleRewardsTakeover.constants'
import useLoyaltyOpenChooseDoubleRewardsFlow from '../hooks/useLoyaltyOpenChooseDoubleRewardsFlow'
import { useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import useLoyaltyChooseDoubleRewardsRegion from '../hooks/useLoyaltyChooseDoubleRewardsRegion'
import useLoyaltyChooseDoubleRewardsTracking from '../hooks/useLoyaltyChooseDoubleRewardsTracking'
import styled from 'styled-components'
import { rem } from 'polished'

const BannerContent = styled.div`
  display: grid;
  align-items: center;
  min-height: ${rem(24)};
`

function renderBold(chunks: ReactNode) {
  return <strong>{chunks}</strong>
}

function LoyaltyChooseDoubleRewardsBannerDescription() {
  const { impressionRef, track } = useLoyaltyChooseDoubleRewardsTracking('header_banner')
  const intl = useIntl()
  const account = useAppSelector(state => state.loyalty.account)
  const loggedIn = useAppSelector(selectLoggedIn)
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const { memberState, endsAt } = useLoyaltyChooseDoubleRewards()
  const { needsRegionSwitch, currency } = useLoyaltyChooseDoubleRewardsRegion()

  const openFlow = useLoyaltyOpenChooseDoubleRewardsFlow()
  const onClick = useCallback(() => track('clicked'), [track])
  const onChoose = useCallback(() => {
    track('clicked')
    openFlow()
  }, [track, openFlow])
  const renderLink = useCallback((chunks: ReactNode) => <TextLink
    ref={impressionRef}
    weight="regular"
    wrap="no-wrap"
    to={`/societe/${CHOOSE_DOUBLE_REWARDS_SLUG}`}
    onClick={onClick}
  >
    {chunks}
  </TextLink>, [impressionRef, onClick])
  const renderChooseLink = useCallback(
    (chunks: ReactNode) => <TextLink ref={impressionRef} weight="regular" wrap="no-wrap" onClick={onChoose}>
      {chunks}
    </TextLink>,
    [impressionRef, onChoose],
  )
  const link = endsAt && (!loggedIn || !account.data || memberState.status === 'unenrolled') ? renderChooseLink : renderLink

  const pointsBannerMessage = intl.formatMessage({
    id: 'xsC5g8uSB7LueOwK21Ou',
    defaultMessage: '<bold>Your next escape, double the Société Points</bold>. <link>Learn more</link>',
    description: 'Page banner for members enrolled in double points with a link to the campaign',
  }, { bold: renderBold, link: renderLink })

  const statusCreditsBannerMessage = intl.formatMessage({
    id: 'BWy_bZnvhBunWBIQC4OI',
    defaultMessage: '<bold>Your next escape, double the Société Status Credits</bold>. <link>Learn more</link>',
    description: 'Page banner for members enrolled in double status credits with a link to the campaign',
  }, { bold: renderBold, link: renderLink })

  const unenrolledBannerMessage = intl.formatMessage({
    id: 'XxvzcGT0glZedN6O0p8y',
    defaultMessage: '<bold>Your next escape, your choice of double rewards.</bold> Double Société Points or Double Status Credits. <link>Choose now</link>',
    description: 'Site-wide double rewards banner with the two options and a link to the campaign',
  }, { bold: renderBold, link })

  const unenrolledInfiniteBannerMessage = intl.formatMessage({
    id: 'zOWthm0doMZXW1iEHEEX',
    defaultMessage: '<bold>Your next escape, double the Société Points.</bold> <link>Choose now</link>',
    description: 'Site-wide double rewards banner for Infinite members who have not activated a reward',
  }, { bold: renderBold, link })

  const switchRegionBannerMessage = currency ? intl.formatMessage({
    id: 'yIp5s7o4McuwMDz2qNlp',
    defaultMessage: '<bold>Your next escape, double rewards.</bold> <link>Switch to {currency}</link>',
    description: 'Site-wide double rewards banner prompting a member to switch to their loyalty currency',
  }, { bold: renderBold, link: renderChooseLink, currency }) : intl.formatMessage({
    id: 'GeZ62X_F7ufWxAEAJfYB',
    defaultMessage: '<bold>Your next escape, double rewards.</bold> <link>Switch region</link>',
    description: 'Site-wide double rewards banner prompting a member to switch to their loyalty region',
  }, { bold: renderBold, link: renderChooseLink })

  let bannerMessage = unenrolledBannerMessage
  if (needsRegionSwitch) {
    bannerMessage = switchRegionBannerMessage
  } else if (memberState.status === 'enrolled') {
    bannerMessage = memberState.choice === 'points' ? pointsBannerMessage : statusCreditsBannerMessage
  } else if (loggedIn && account.data && tier === 'infinite') {
    bannerMessage = unenrolledInfiniteBannerMessage
  }

  return <BannerContent>
    <span>
      {bannerMessage}
    </span>
  </BannerContent>
}

export default LoyaltyChooseDoubleRewardsBannerDescription
